import { useChatmessageModel } from '@/entities/chatmessage';
import { FileInBase64, fileTob64, useGetSalespointList } from '@/shared';
import { useGlobalToast as useToast } from '@/shared/lib/toast';
import {
  MessageUpdateWithGroupId,
  MessageUpdateWithSalespointIds,
  useGetBrokerclientList
} from 'simpleloan_api';
import { computed, ref, watch } from 'vue';

/**
 * Модель для создания глобального сообщения в чате поддержки, которое будет отправлено всем/выбранным ТТ или КБО
 */
export const useModel = () => {
  const chatmessageModel = useChatmessageModel();
  const toast = useToast();

  /**
   * Текст сообщения
   */
  const message = ref<string | null>(null);

  /**
   * Прикрепленный Файл
   */
  const file = ref<File | null>(null);

  /**
   * Группа получателей
   */
  const receiversScope = ref<'all' | 'selected' | 'except_selected'>('all');

  /**
   * Метод распределения сообщений
   */
  const distributionMethod = ref<'salespoint' | 'brokerclient' | 'group' | null>(null);

  /**
   * Список торговых точек в которые будет отправлено сообщение
   */
  const salespoint_ids = ref<number[] | null>(null);

  /**
   * Список всех торговых точек отсортированных по mortgage_point
   */
  const salespointIdListSortedByMortgage = ref<number[] | null>(null);

  /**
   * Список групп торговых точек в которые будет отправлено сообщение
   */
  const group_ids = ref<number[] | null>(null);

  // /**
  //  * Список КБО в которые будет отправлено сообщение
  //  */
  // const brokerclient_ids = ref<number[] | null>(null)

  /**
   * Индикатор отправки глобального сообщения
   */
  const sendGlobalMessagePending = ref<boolean>(false);

  /**
   * Индикатор, что форма заблокирована
   */
  const formDisabled = computed(() => sendGlobalMessagePending.value);

  /**
   * Причина блокировки метода отправки глобального сообщения
   */
  const sendGlobalMessageDisabledReason = computed(() => {
    if (receiversScope.value === 'selected') {
      if (!distributionMethod.value) {
        return 'Не выбран "Фильтровать по"';
      }

      if (distributionMethod.value === 'group' && !group_ids.value) {
        return 'Не выбрана группа торговых точек';
      }

      if (
        distributionMethod.value === 'salespoint' &&
        (!salespoint_ids.value || !salespoint_ids.value?.length)
      ) {
        return 'Не выбраны торговые точки';
      }
    }

    if (!message.value && !file.value) {
      return 'Нет ни текста сообщения, ни прикрепленного файла';
    }

    return null;
  });

  /**
   * Индикатор блокировки метода отправки глобального сообщения
   */
  const sendGlobalMessageDisabled = computed(() => Boolean(sendGlobalMessageDisabledReason.value));

  /**
   * Следим за изменением группы получателей
   */
  watch(receiversScope, receiversScope => {
    if (receiversScope === 'all') {
      // При выборе "Всех" нужно очистить поля, которые ограничивают список
      distributionMethod.value = null;
      salespoint_ids.value = null;
      group_ids.value = null;
    }
  });

  /**
   * Следим за изменением метода распределения сообщений
   */
  watch(distributionMethod, distributionMethod => {
    if (distributionMethod === 'group') {
      // При выборе метода, который будет фильтровать по КБО, нужно очистить список выбранных ТТ
      salespoint_ids.value = null;
    }

    if (distributionMethod === 'salespoint') {
      // При выборе метода, который будет фильтровать по ТТ, нужно очистить список выбранных КБО
      group_ids.value = null;
    }
  });

  /**
   * Отправка глобального сообщения
   */
  async function sendGlobalMessage() {
    if (sendGlobalMessageDisabled.value) {
      toast.add({
        severity: 'warn',
        summary: 'Отправка глобального сообщения',
        detail: sendGlobalMessageDisabledReason.value
      });
      return;
    }

    const globalMessage: {
      salespoint_ids?: number[];
      group_ids?: number[];
      message?: string;
      file?: FileInBase64;
    } = {};

    if (message.value) {
      globalMessage.message = message.value;
    }

    if (file.value) {
      // Переводим файл в формат base64 для отправки на бекенд
      const fileInBase64 = await fileTob64(file.value);
      globalMessage.file = fileInBase64;
    }

    if (salespoint_ids.value) {
      globalMessage.salespoint_ids = salespoint_ids.value;
    }

    /**
     * Если сообщение отправляется всем, то получаем список ипотечных точек
     * и отправляем им
     */
    if (receiversScope.value === 'all') {
      //** Если список отсортированных точек уже был получен ранее */
      if (salespointIdListSortedByMortgage.value) {
        globalMessage.salespoint_ids = salespointIdListSortedByMortgage.value;
      } else {
        //** Если список отсортированных точек ранее получен не был*/
        const res = await getSalespointList();

        if (res) {
          globalMessage.salespoint_ids = res;
        } else {
          //** Если список отфильтрованных тт получен не был останавливаем скрипт */
          return;
        }
      }
    }

    if (group_ids.value) {
      globalMessage.group_ids = group_ids.value;
    }

    sendGlobalMessagePending.value = true;
    const res = await chatmessageModel.updateMessage.post(
      globalMessage as MessageUpdateWithSalespointIds | MessageUpdateWithGroupId
    );

    const summary = 'Отправка глобально сообщения';
    if (res?._code === 0 && res._data) {
      resetForm();
      toast.add({ severity: 'success', summary });
    } else {
      toast.add({ severity: 'error', summary, detail: res?._message });
    }

    sendGlobalMessagePending.value = false;

    return res;
  }

  /**
   * Сброс значений формы до начальных
   */
  function resetForm() {
    receiversScope.value = 'all';
    message.value = null;
    file.value = null;
    distributionMethod.value = null;
    salespoint_ids.value = null;
    group_ids.value = null;
  }

  /**
   * Получение списка id ипотечных точек
   *  Для сценария *Отправить сообщения всем*
   */
  const getSalespointList = async () => {
    const res = await useGetSalespointList().post({
      _filter: { mortgage_point: true },
      _fields: ['id'],
      _page_size: 10000000
    });

    if (res?._data) {
      salespointIdListSortedByMortgage.value = res._data.map(item => item.id);
    } else {
      toast.add({
        severity: 'error',
        summary: 'Ошибка при получении списка торговых точек',
        detail: res?._message
      });
    }

    return salespointIdListSortedByMortgage.value;
  };

  return {
    file,
    message,
    formDisabled,
    receiversScope,
    distributionMethod,
    salespoint_ids,
    group_ids,
    sendGlobalMessagePending,
    sendGlobalMessageDisabledReason,
    sendGlobalMessageDisabled,
    sendGlobalMessage
  };
};
